import React, { useRef, useState, useContext, useEffect, useReducer } from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loader } from 'graphql.macro';
import { useQuery, useMutation, useSubscription } from 'react-apollo-hooks';
import { format } from 'date-fns';
import cn from 'classnames';
import debounce from 'debounce';

import { buildRestApiUrl, ProfileContext, suppressError } from '@loyalup/core';
import { WithTitle, Button, Select, Input, DownloadLink, FileUploader, Modal, PageSize, Table } from '@loyalup/ui';

import ClientForm from 'components/ClientForm';
import ColumnVisibility from 'components/ColumnVisibility';
import ClientGroupActions from 'components/ClientGroupActions';
import Filters from 'components/Filters';
import Filter from 'components/Filter';
import { getClientFields } from 'components/ClientFields';

import useToken from 'utils/hooks/useToken';
import updateProfileInCache from 'utils/updateProfileInCache';

import { ReactComponent as DocsIcon } from 'icons/docs.svg';
import { ReactComponent as PlusIcon } from 'icons/plus.svg';
import { ReactComponent as UploadIcon } from 'icons/upload.svg';
import { ReactComponent as CsvIcon } from 'icons/csv.svg';
import { ReactComponent as RightArrowIcon } from 'icons/right-arrow.svg';
import { ReactComponent as SearchIcon } from 'icons/search.svg';

import s from './Clients.module.css';

const CLIENTS_QUERY = loader('../../gql/queries/Clients.gql');
const CREATE_IMPORT_MUTATION = loader('../../gql/mutations/CreateClientImport.gql');
const SET_TABLE_VIEW_SETTINGS_MUTATION = loader('../../gql/mutations/SetTableViewSettings.gql');
const CLIENT_UPDATES_SUBSCRIPTION = loader('../../gql/subscriptions/clientUpdated.gql');

const Clients = ({ history }) => {
  const { clientsCount, company, clientsTableSettings } = useContext(ProfileContext);
  const anyClients = clientsCount > 0;

  const { pageSize, orderBy, orderDirection, visibleColumns } = clientsTableSettings;

  const [defaultTableConfig] = useState({ offset: 0, limit: pageSize, orderBy, orderDirection });

  const uploadRef = useRef();
  const [t] = useTranslation();
  const csvToken = useToken('clients_export');

  const [uploading, setUploading] = useState(false);

  const reducer = (state, action) => {
    const { tableConfig, clientsSelection } = state;
    const noSelection = { include: [], exclude: [] };
    switch (action.type) {
      case 'setFilter':
        return { ...state, filter: action.filter };
      case 'setClient':
        return { ...state, client: action.client };
      case 'resetClient':
        return {
          ...state,
          client: false,
        };
      case 'afterSaveClient':
        return {
          ...state,
          client: false,
          tableConfig: { ...tableConfig, offset: state.client.id ? tableConfig.offset : 0 },
        };
      case 'newClient':
        return { ...state, client: { gender: 'UNKNOWN' } };
      case 'setColumns':
        return { ...state, columns: action.columns };
      case 'setLimit':
        return { ...state, tableConfig: { ...tableConfig, limit: action.limit } };
      case 'search':
        return {
          ...state,
          text: action.text,
          clientsSelection: { ...clientsSelection, ...noSelection, text: action.text },
          tableConfig: { ...tableConfig, offset: 0 },
        };
      case 'setTempFilter':
        return {
          ...state,
          tempFilterId: action.tempFilterId,
          clientsSelection: { ...clientsSelection, ...noSelection, conds: action.conds },
          tableConfig: { ...tableConfig, offset: 0 },
        };
      case 'setTableConfig':
        return { ...state, tableConfig: action.config };
      case 'resetClientsSelection':
        return { ...state, clientsSelection: { ...clientsSelection, ...noSelection } };
      case 'setClientsSelection':
        return { ...state, clientsSelection: { ...clientsSelection, ...action.selection } };
      default:
        throw new Error();
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    filter: undefined,
    tempFilterId: undefined,
    client: false,
    text: undefined,
    columns: visibleColumns,
    tableConfig: defaultTableConfig,
    clientsSelection: { include: [], exclude: [], text: undefined, conds: undefined },
  });

  const columns = getClientFields({ asList: true, filter: state.columns });

  const { loading, data, fetchMore } = useQuery(CLIENTS_QUERY, {
    suspend: false,
    variables: defaultTableConfig,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    const { tableConfig } = state;
    if (
      clientsTableSettings.orderBy !== tableConfig.orderBy ||
      clientsTableSettings.orderDirection !== tableConfig.orderDirection
    ) {
      setTableViewSettings({ orderBy, orderDirection });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tableConfig.orderBy, state.tableConfig.orderDirection]);

  const queryVars = {
    filterId: state.tempFilterId,
    text: state.text,
    ...state.tableConfig,
  };

  const loadClients = async () => {
    try {
      await fetchMore({
        variables: queryVars,
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return fetchMoreResult;
        },
      });
    } catch (e) {
      suppressError(e, 'Invariant Violation: 15');
    }
  };

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tableConfig]);

  const [createImport] = useMutation(CREATE_IMPORT_MUTATION, {
    update: (cache, { data: { createClientImport } }) => {
      const { sid } = createClientImport;
      history.push(`/clients/imports/${sid}`);
    },
  });

  const setTableViewSettings = tableViewSettings => {
    setClientOrderMutation({ variables: { table: 'CLIENTS', tableViewSettings } });
  };

  const [setClientOrderMutation] = useMutation(SET_TABLE_VIEW_SETTINGS_MUTATION, {
    update: (cache, { data: { setTableViewSettings } }) => {
      updateProfileInCache(cache, { clientsTableSettings: setTableViewSettings });
    },
  });

  const { data: updates } = useSubscription(CLIENT_UPDATES_SUBSCRIPTION, { variables: { companyId: company.id } });

  return (
    <WithTitle title={t('Клиенты')}>
      <div className={s.header}>
        <h1>{t('Клиенты')}</h1>
        <div className={s.spacer} />
        {anyClients && (
          <div className={s.toolbarItem}>
            <Input
              theme="toolbar-white"
              expandWidth={300}
              leftIcon={<SearchIcon />}
              type="text"
              placeholder={t('Поиск')}
              onChange={debounce(text => dispatch({ text, type: 'search' }), 300)}
            />
          </div>
        )}
        {anyClients && (
          <Select
            theme="green"
            className={s.toolbarItem}
            loading={uploading}
            label={t('Добавить клиентов')}
            items={[
              { label: t('Добавить вручную'), onClick: () => dispatch({ type: 'newClient' }) },
              { label: t('Импорт из CSV'), onClick: () => uploadRef.current.selectFile() },
            ]}
          />
        )}
        <FileUploader
          hidden
          accept="text/csv"
          type="csv"
          server={process.env.REACT_APP_SERVER_URL}
          onStart={() => setUploading(true)}
          onFinish={({ filename, path }) => createImport({ variables: { filename, path } })}
          ref={uploadRef}
        />
      </div>
      {anyClients && (
        <div className={s.toolbar}>
          <Filters
            table="CLIENTS"
            theme="toolbar-white"
            className={s.toolbarItem}
            stretch
            fixedWidth={220}
            activeFilter={state.filter}
            onSelect={filter => dispatch({ filter, type: 'setFilter' })}
          />
          <ClientGroupActions
            className={s.toolbarItem}
            disabled={uploading}
            selection={state.clientsSelection}
            onActionFinish={() => {
              dispatch({ type: 'resetClientsSelection' });
              loadClients();
            }}
            queryVars={queryVars}
          />
          <div className={s.spacer} />
          <PageSize
            table="CLIENTS"
            className={s.toolbarItem}
            theme="toolbar-white"
            selected={state.tableConfig.limit}
            onChange={limit => dispatch({ limit, type: 'setLimit' })}
            afterSet={updateProfileInCache}
          />
          <ColumnVisibility
            table="CLIENTS"
            className={s.toolbarItem}
            fixedWidth={200}
            theme="toolbar-white"
            stretch
            visibleColumns={state.columns}
            onChange={columns => dispatch({ columns, type: 'setColumns' })}
          />
          <DownloadLink
            className={s.toolbarItem}
            theme="toolbar"
            element="a"
            icons="first"
            href={buildRestApiUrl(`/clients/export/${format(new Date(), 'y-MM-d')}.csv`, {
              filter_id: state.filter?.id,
              text: state?.text,
              token: csvToken,
            })}
          >
            <RightArrowIcon />
            {t('Экспорт в CSV')}
          </DownloadLink>
        </div>
      )}
      {anyClients && (
        <Filter
          table="CLIENTS"
          className={cn(s.filter, { [s.visible]: !!state.filter })}
          filter={state.filter}
          onUpdate={filter => dispatch({ filter, type: 'setFilter' })}
          setTempFilter={(tempFilterId, conds) => dispatch({ tempFilterId, conds, type: 'setTempFilter' })}
        />
      )}
      {anyClients ? (
        <Table
          selectable
          selection={state.clientsSelection}
          total={data?.clients?.total}
          tableConfig={state.tableConfig}
          columns={columns}
          rows={data?.clients?.clients || []}
          loading={loading}
          highlightText={state.tableConfig.text}
          onSelectionChange={selection => dispatch({ selection, type: 'setClientsSelection' })}
          onRowDoubleClick={client => dispatch({ client, type: 'setClient' })}
          onChange={config => dispatch({ config, type: 'setTableConfig' })}
          highlightRow={updates && updates.clientUpdated}
        />
      ) : (
        <div className={s.placeholderContainer}>
          <div className={s.placeholderButtons}>
            <Button theme="square" onClick={() => dispatch({ type: 'newClient' })}>
              <PlusIcon />
              <DocsIcon />
              <span>{t('Добавить вручную')}</span>
            </Button>
            <Button theme="square" onClick={() => uploadRef.current.selectFile()} loading={uploading}>
              <UploadIcon />
              <CsvIcon />
              <span>{t('Загрузить файл')}</span>
            </Button>
          </div>
          <div className={s.placeholderText}>
            {t('У вас пока еще нет добавленных клиентов, вы можете добавить их вручную или загрузить из CSV-файла')}
          </div>
        </div>
      )}
      <Modal
        title={(state.client || {}).id ? state.client.fullName : t('Добавить вручную')}
        isOpen={!!state.client}
        onClose={() => dispatch({ type: 'resetClient' })}
      >
        <ClientForm
          client={state.client || {}}
          afterSave={() => dispatch({ type: 'afterSaveClient' })}
          onCancel={() => dispatch({ type: 'resetClient' })}
        />
      </Modal>
    </WithTitle>
  );
};

export default withRouter(Clients);
